// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Builder, Config, ImmutableTree, JsonLogicTree, Query, Utils as QbUtils, AntdConfig, AntdWidgets,
} from '@react-awesome-query-builder/antd';

import { omit } from 'lodash';
import { DownOutlined } from '@ant-design/icons';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import {
    CombinedState, GetObjectTypeChinese, ObjectType, ShapeType,
} from 'reducers';
import { Label } from 'cvat-core-wrapper';
import { changeAnnotationsFilters, fetchAnnotationsAsync, showFilters } from 'actions/annotation-actions';

const { FieldDropdown } = AntdWidgets;

const FILTERS_HISTORY = 'annotationFiltersHistory';
const defaultTree = QbUtils.loadTree({ type: 'group', id: QbUtils.uuid() });

interface StoredFilter {
    id: string;
    logic: JsonLogicTree;
}

const getConvertedInputType = (inputType: string): string => {
    switch (inputType) {
        case 'checkbox':
            return 'boolean';
        case 'radio':
            return 'select';
        default:
            return inputType;
    }
};

const adjustName = (name: string): string => name.replace(/\./g, '\u2219');

const getAttributesSubfields = (labels: Label[]): Record<string, any> => {
    const subfields: Record<string, any> = {};
    labels.forEach((label: any): void => {
        const adjustedLabelName = adjustName(label.name);
        subfields[adjustedLabelName] = {
            type: '!struct', // nested complex field
            label: label.name,
            subfields: {},
        };

        const labelSubfields = subfields[adjustedLabelName].subfields;
        label.attributes.forEach((attr: any): void => {
            const adjustedAttrName = adjustName(attr.name);
            labelSubfields[adjustedAttrName] = {
                label: attr.name,
                type: getConvertedInputType(attr.inputType),
            };
            if (labelSubfields[adjustedAttrName].type === 'select') {
                labelSubfields[adjustedAttrName] = {
                    ...labelSubfields[adjustedAttrName],
                    fieldSettings: {
                        listValues: attr.values,
                    },
                };
            }
        });
    });

    return subfields;
};

function FiltersModalComponent(): JSX.Element {
    const labels = useSelector((state: CombinedState) => state.annotation.job.labels);
    const activeFilters = useSelector((state: CombinedState) => state.annotation.annotations.filters);
    const visible = useSelector((state: CombinedState) => state.annotation.filtersPanelVisible);
    const [config, setConfig] = useState<Config>(AntdConfig);

    const dispatch = useDispatch();
    const [immutableTree, setImmutableTree] = useState<ImmutableTree>(defaultTree);
    const [filters, setFilters] = useState([] as StoredFilter[]);

    useEffect(() => {
        const initialConfig = {
            ...AntdConfig,
            fields: {
                label: {
                    label: '标签',
                    type: 'select',
                    valueSources: ['value'] as ('value')[],
                    fieldSettings: {
                        listValues: labels.map((label: any) => ({
                            value: label.name,
                            title: label.name,
                        })),
                    },
                },
                type: {
                    label: '类型',
                    type: 'select',
                    fieldSettings: {
                        listValues: [
                            { value: 'shape', title: GetObjectTypeChinese(ObjectType.SHAPE) },
                            { value: 'track', title: GetObjectTypeChinese(ObjectType.TRACK) },
                            { value: 'tag', title: GetObjectTypeChinese(ObjectType.TAG) },
                        ],
                    },
                },
                shape: {
                    label: '形状',
                    type: 'select',
                    fieldSettings: {
                        listValues: [
                            { value: 'rectangle', title: GetObjectTypeChinese(ShapeType.RECTANGLE) },
                            { value: 'points', title: GetObjectTypeChinese(ShapeType.POINTS) },
                            { value: 'polyline', title: GetObjectTypeChinese(ShapeType.POLYLINE) },
                            { value: 'Polygon', title: GetObjectTypeChinese(ShapeType.POLYGON) },
                            { value: 'cuboid', title: GetObjectTypeChinese(ShapeType.CUBOID) },
                            { value: 'ellipse', title: GetObjectTypeChinese(ShapeType.ELLIPSE) },
                            { value: 'skeleton', title: GetObjectTypeChinese(ShapeType.SKELETON) },
                            { value: 'mask', title: GetObjectTypeChinese(ShapeType.MASK) },
                        ],
                    },
                },
                occluded: {
                    label: '是否遮挡',
                    type: 'boolean',
                },
                width: {
                    label: '宽度',
                    type: 'number',
                    fieldSettings: { min: 0 },
                },
                height: {
                    label: '高度',
                    type: 'number',
                    fieldSettings: { min: 0 },
                },
                objectID: {
                    label: '标注标识',
                    type: 'number',
                    hideForCompare: true,
                    fieldSettings: { min: 0 },
                },
                serverID: {
                    label: '服务标识',
                    type: 'number',
                    hideForCompare: true,
                    fieldSettings: { min: 0 },
                },
                attr: {
                    label: '属性',
                    type: '!struct',
                    subfields: getAttributesSubfields(labels),
                    fieldSettings: {
                        treeSelectOnlyLeafs: true,
                    },
                },
            },
            settings: {
                ...AntdConfig.settings,
                renderField: (_props: any) => (
                    <FieldDropdown {...omit(_props)} customProps={omit(_props.customProps, 'showSearch')} />
                ),
                // using FieldDropdown because we cannot use antd because of antd-related bugs
                // https://github.com/ukrbublik/react-awesome-query-builder/issues/224
            },
        };

        setConfig(initialConfig);
        const filtersHistory = window.localStorage.getItem(FILTERS_HISTORY)?.trim() || '[]';
        try {
            setFilters(JSON.parse(filtersHistory));
        } catch (_) {
            setFilters([]);
        }
    }, []);

    useEffect(() => {
        window.localStorage.setItem(FILTERS_HISTORY, JSON.stringify(filters));
    }, [filters]);

    useEffect(() => {
        if (visible) {
            try {
                if (activeFilters.length) {
                    const tree = QbUtils.loadFromJsonLogic(activeFilters[0], config);
                    if (tree) {
                        const treeFromActiveFilters = QbUtils.checkTree(tree, config);
                        setImmutableTree(treeFromActiveFilters);
                    } else {
                        throw new Error();
                    }
                } else {
                    throw new Error();
                }
            } catch (_: any) {
                setImmutableTree(defaultTree);
            }
        }
    }, [visible]);

    const applyFilters = (filtersData: any[]): void => {
        dispatch(changeAnnotationsFilters(filtersData));
        dispatch(fetchAnnotationsAsync());
        dispatch(showFilters(false));
    };

    const confirmModal = (): void => {
        const currentFilter: StoredFilter = {
            id: QbUtils.uuid(),
            logic: QbUtils.jsonLogicFormat(immutableTree, config).logic || {},
        };
        const updatedFilters = filters.filter(
            (filter) => JSON.stringify(filter.logic) !== JSON.stringify(currentFilter.logic),
        );
        setFilters([currentFilter, ...updatedFilters].slice(0, 10));
        applyFilters([currentFilter.logic]);
    };

    const isModalConfirmable = (): boolean => (
        (QbUtils.queryString(immutableTree, config) || '')
            .trim().length > 0 && QbUtils.isValidTree(immutableTree)
    );

    const renderBuilder = (builderProps: any): JSX.Element => (
        <div className='query-builder-container'>
            <div className='query-builder'>
                <Builder {...builderProps} />
            </div>
        </div>
    );

    const onChange = (tree: ImmutableTree): void => {
        setImmutableTree(tree);
    };

    const menu = (
        <Menu>
            {filters
                .map((filter: StoredFilter) => {
                    // if a logic received from local storage does not correspond to current config
                    // which depends on label specification
                    // (it can be when history from another task with another specification or when label was removed)
                    // loadFromJsonLogic() prints a warning to console
                    // the are not ways to configure this behaviour

                    const tree = QbUtils.loadFromJsonLogic(filter.logic, config);
                    if (tree) {
                        const queryString = QbUtils.queryString(tree, config);
                        return { tree, queryString, filter };
                    }

                    return { tree, queryString: null, filter };
                })
                .filter(({ queryString }) => !!queryString)
                .map(({ filter, tree, queryString }) => (
                    <Menu.Item key={filter.id} onClick={() => setImmutableTree(tree as ImmutableTree)}>
                        {queryString}
                    </Menu.Item>
                ))}
        </Menu>
    );

    return (
        <Modal
            className={visible ? 'cvat-filters-modal cvat-filters-modal-visible' : 'cvat-filters-modal'}
            visible={visible}
            closable={false}
            width={800}
            destroyOnClose
            centered
            onCancel={() => dispatch(showFilters(false))}
            footer={[
                <Button
                    key='clear'
                    disabled={!activeFilters.length}
                    onClick={() => applyFilters([])}
                    className='cvat-filters-modal-clear-button'
                >
                    清除过滤规则
                </Button>,
                <Button
                    key='cancel'
                    onClick={() => dispatch(showFilters(false))}
                    className='cvat-filters-modal-cancel-button'
                >
                    取消
                </Button>,
                <Button
                    key='submit'
                    type='primary'
                    disabled={!isModalConfirmable()}
                    onClick={confirmModal}
                    className='cvat-filters-modal-submit-button'
                >
                    提交
                </Button>,
            ]}
        >
            <div
                key='used'
                className='recently-used-wrapper'
                style={{ display: filters.length ? 'inline-block' : 'none' }}
            >
                <Dropdown overlay={menu}>
                    <Button
                        type='text'
                        className='cvat-filters-modal-recently-used-button'
                    >
                        最近使用
                        {' '}
                        <DownOutlined />
                    </Button>
                </Dropdown>
            </div>
            { !!config.fields && (
                <Query
                    {...config}
                    value={immutableTree as ImmutableTree}
                    onChange={onChange}
                    renderBuilder={renderBuilder}
                />
            )}
        </Modal>
    );
}

export default React.memo(FiltersModalComponent);
